import { animate } from "motion/mini"

const MOBILE = "(max-width: 767px)"

const CHAOS_ROTATIONS = {
  wa1: -4, wa2: 5, email1: -6, phone1: 3, sheet: -3, wa3: 7
}

// Channel cards that FLIP-slide to flow positions
const CHANNEL_MAP = { wa1: 0, email1: 1, phone1: 2 }

// Problem cards that dissolve (Krated eliminates these)
const DISSOLVE_IDS = ["wa2", "sheet", "wa3"]

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function clearChildren(el) {
  while (el.firstChild) el.removeChild(el.firstChild)
}

export function initHeroAnimation() {
  const container = document.querySelector("[data-hero-flow]")
  if (!container) return

  const chaosCards = container.querySelectorAll(".chaos-card")
  const flowPhase = container.querySelector("[data-flow]")
  const flowMsgs = flowPhase.querySelectorAll(".flow-msg")
  const flowNodes = flowPhase.querySelectorAll(".flow-node")
  const inbox = flowPhase.querySelector("[data-node='inbox']")
  const outcomes = flowPhase.querySelectorAll(".flow-outcome")
  const svgEl = flowPhase.querySelector(".flow-connectors")
  const orderMetrics = container.querySelector("[data-flow-metrics]")
  const chaosMetrics = container.querySelector("[data-chaos-metrics]")

  // Build card lookup by data-chaos id
  const chaosCardMap = {}
  chaosCards.forEach(card => { chaosCardMap[card.dataset.chaos] = card })

  // Reduced motion: show flow statically, skip chaos entirely
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    flowPhase.style.opacity = "1"
    flowNodes.forEach(el => {
      el.style.opacity = "1"
      el.style.transform = "none"
    })
    if (orderMetrics) orderMetrics.style.opacity = "1"
    drawConnectors()
    svgEl.querySelectorAll("path").forEach(p => {
      p.style.opacity = "1"
    })
    return
  }

  // Build an orthogonal path with rounded corners: H → bend → V → bend → H
  function orthoPath(from, to, cornerR) {
    const r = cornerR || 8
    const dy = to.y - from.y
    const midX = (from.x + to.x) / 2

    // Same level -- straight horizontal line
    if (Math.abs(dy) < 2) {
      return `M ${from.x} ${from.y} H ${to.x}`
    }

    // Too short for corners -- straight line fallback
    if (Math.abs(dy) < r * 2 || (midX - from.x) < r || (to.x - midX) < r) {
      return `M ${from.x} ${from.y} L ${to.x} ${to.y}`
    }

    const s = dy > 0 ? 1 : -1
    return [
      `M ${from.x} ${from.y}`,
      `H ${midX - r}`,
      `Q ${midX} ${from.y}, ${midX} ${from.y + r * s}`,
      `V ${to.y - r * s}`,
      `Q ${midX} ${to.y}, ${midX + r} ${to.y}`,
      `H ${to.x}`
    ].join(" ")
  }

  // Compute SVG paths between flow-msgs → inbox and inbox → outcomes
  function drawConnectors() {
    clearChildren(svgEl)
    const base = flowPhase.getBoundingClientRect()

    function rel(el, side) {
      const rect = el.getBoundingClientRect()
      const y = rect.top + rect.height / 2 - base.top
      if (side === "right") return { x: rect.right - base.left, y }
      return { x: rect.left - base.left, y }
    }

    const hubLeft = rel(inbox, "left")
    const hubRight = rel(inbox, "right")

    // Input paths: right edge of each flow-msg → left edge of hub
    flowMsgs.forEach(msg => {
      const from = rel(msg, "right")
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
      path.setAttribute("d", orthoPath(from, hubLeft))
      path.dataset.dir = "in"
      svgEl.appendChild(path)
    })

    // Output paths: right edge of hub → left edge of each outcome's first station
    outcomes.forEach(outcome => {
      const target = outcome.querySelector(".flow-station")
      const to = rel(target, "left")
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
      path.setAttribute("d", orthoPath(hubRight, to))
      path.dataset.dir = "out"
      svgEl.appendChild(path)
    })
  }

  function reset() {
    chaosCards.forEach(el => {
      el.style.opacity = "0"
      el.style.transform = "scale(0.7)"
    })
    if (chaosMetrics) chaosMetrics.style.opacity = "0"
    flowPhase.style.opacity = "0"
    flowNodes.forEach(el => {
      el.style.opacity = "0"
      el.style.transform = ""
    })
    clearChildren(svgEl)
    if (orderMetrics) orderMetrics.style.opacity = "0"
    container.style.backgroundColor = ""
  }

  // Measure FLIP deltas: where each channel card needs to fly to
  function measureFLIP() {
    const deltas = {}
    for (const [chaosId, flowIdx] of Object.entries(CHANNEL_MAP)) {
      const chaosRect = chaosCardMap[chaosId].getBoundingClientRect()
      const flowRect = flowMsgs[flowIdx].getBoundingClientRect()
      deltas[chaosId] = {
        dx: (flowRect.left + flowRect.width / 2) - (chaosRect.left + chaosRect.width / 2),
        dy: (flowRect.top + flowRect.height / 2) - (chaosRect.top + chaosRect.height / 2)
      }
    }
    return deltas
  }

  async function playLoop() {
    // === BEAT 1: CHAOS (0 ~ 4s) ===
    container.style.backgroundColor = "#fefbf7"

    const cardOrder = Array.from(chaosCards)
    for (let i = 0; i < cardOrder.length; i++) {
      const card = cardOrder[i]
      const rot = CHAOS_ROTATIONS[card.dataset.chaos] || 0
      animate(card, {
        opacity: [0, 1],
        transform: [`scale(0.7) rotate(${rot}deg)`, `scale(1) rotate(${rot}deg)`]
      }, { duration: 0.4, easing: [0.34, 1.2, 0.64, 1] })
      if (i < cardOrder.length - 1) await sleep(300)
    }

    // Chaos metrics fade in
    if (chaosMetrics) {
      animate(chaosMetrics, { opacity: [0, 1] }, { duration: 0.4, easing: "ease-out" })
    }

    // Hold -- visitor absorbs the mess
    await sleep(2400)

    // === BEAT 2: TRANSFORM (all simultaneous) ===
    const deltas = measureFLIP()

    // Reveal flow container (individual children still opacity:0)
    flowPhase.style.opacity = "1"

    // Background tint: warm → cool
    animate(container, {
      backgroundColor: ["#fefbf7", "#f8fafc"]
    }, { duration: 0.8, easing: "ease-in-out" })

    // Chaos metrics out
    if (chaosMetrics) {
      animate(chaosMetrics, { opacity: [1, 0] }, { duration: 0.3, easing: "ease-in" })
    }

    // Krated hub drops in (spring bounce)
    animate(inbox, {
      opacity: [0, 1],
      transform: ["scale(0)", "scale(1)"]
    }, { duration: 0.5, easing: [0.34, 1.56, 0.64, 1] })

    // Channel cards: FLIP-slide to flow positions + cross-fade
    for (const [chaosId, flowIdx] of Object.entries(CHANNEL_MAP)) {
      const card = chaosCardMap[chaosId]
      const flowMsg = flowMsgs[flowIdx]
      const d = deltas[chaosId]
      const rot = CHAOS_ROTATIONS[chaosId] || 0

      animate(card, {
        transform: [
          `translate(0px, 0px) scale(1) rotate(${rot}deg)`,
          `translate(${d.dx}px, ${d.dy}px) scale(1) rotate(0deg)`
        ]
      }, { duration: 0.8, easing: [0.4, 0, 0.2, 1] })

      animate(card, { opacity: [1, 0] }, { duration: 0.2, delay: 0.6 })
      animate(flowMsg, { opacity: [0, 1] }, { duration: 0.2, delay: 0.6 })
    }

    // Problem cards: shrink + dissolve
    for (const id of DISSOLVE_IDS) {
      const card = chaosCardMap[id]
      if (!card) continue
      const rot = CHAOS_ROTATIONS[id] || 0
      animate(card, {
        opacity: [1, 0],
        transform: [`scale(1) rotate(${rot}deg)`, `scale(0.3) rotate(${rot}deg)`]
      }, { duration: 0.8, easing: "ease-in" })
      animate(card, {
        borderColor: ["#e2e8f0", "#fca5a5", "transparent"]
      }, { duration: 0.8, easing: "ease-in" })
    }

    // Wait for longest transform animation
    await sleep(900)

    // === BEAT 3: FLOW BUILDS ===
    const mobile = window.matchMedia(MOBILE).matches

    if (!mobile) {
      // Draw SVG connector paths now that layout is stable
      drawConnectors()
      const inPaths = svgEl.querySelectorAll("[data-dir='in']")
      const outPaths = svgEl.querySelectorAll("[data-dir='out']")

      // Stagger input connectors (channels → Krated)
      for (let i = 0; i < inPaths.length; i++) {
        animate(inPaths[i], { opacity: [0, 1] }, { duration: 0.3, easing: "ease-out" })
        inPaths[i].classList.add("flowing")
        if (i < inPaths.length - 1) await sleep(150)
      }
      await sleep(250)

      // Stagger output connectors (Krated → outcomes)
      for (let i = 0; i < outPaths.length; i++) {
        animate(outPaths[i], { opacity: [0, 1] }, { duration: 0.3, easing: "ease-out" })
        outPaths[i].classList.add("flowing")
        if (i < outPaths.length - 1) await sleep(150)
      }
      await sleep(200)

      // Stagger outcome rows in
      for (let i = 0; i < outcomes.length; i++) {
        animate(outcomes[i],
          { opacity: [0, 1], transform: ["translateX(16px)", "translateX(0)"] },
          { duration: 0.35, easing: "ease-out" }
        )
        if (i < outcomes.length - 1) await sleep(200)
      }
      await sleep(350)
    }

    // Order metrics fade in
    if (orderMetrics) {
      await animate(orderMetrics, { opacity: [0, 1] }, { duration: 0.5, easing: "ease-out" }).finished
    }

    // === BEAT 4: HOLD → FADE → RESET → LOOP ===
    await sleep(3500)

    const allVisible = [...flowNodes, flowPhase, ...svgEl.querySelectorAll("path")]
    if (orderMetrics) allVisible.push(orderMetrics)
    await animate(allVisible, { opacity: 0 }, { duration: 0.6, easing: "ease-in" }).finished

    await sleep(500)
    reset()
    playLoop()
  }

  // Start on intersection
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        observer.disconnect()
        reset()
        playLoop()
      }
    },
    { threshold: 0.3 }
  )
  observer.observe(container)
}
